import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "../redux";
import { updateDownloadJobStatus } from "../redux/reducers/downloadJobs.reducer";
import downloadServices from "../redux/services/download.services";
import { IDownloadReportSSEEventData } from "../types";
import { BASE_API_URL, getAPIHeaderOptions } from "../utils/apiUtils";

export function useDownloadJobsManager() {
	const dispatch = useAppDispatch();

	const downloadJobs = useAppSelector((state) => state.downloadJobs.jobs);

	const activeConnections = useRef<Set<string>>(new Set());

	const controller = new AbortController();

	// Function to handle download completion
	function handleDownloadReport(jobID: string) {
		downloadServices
			.handleDownloadReport(jobID)
			.then(() => {
				dispatch(
					updateDownloadJobStatus({
						jobId: jobID,
						status: "completed",
						progress: 100,
						message: "Download complete"
					})
				);

				// Remove from active connections
				activeConnections.current.delete(jobID);
			})
			.catch((error) => {
				dispatch(
					updateDownloadJobStatus({
						jobId: jobID,
						status: "failed",
						error: "data" in error && error.data ? String(error.data) : "Download failed"
					})
				);

				// Remove from active connections
				activeConnections.current.delete(jobID);
			});
	}

	async function connectSSE(url: string, onMessage: (event: any) => void, onAbort: () => void) {
		const headers = {
			...getAPIHeaderOptions(true),
			Accept: "text/event-stream",
			"Cache-Control": "no-cache"
		};

		const timer = setTimeout(() => {
			controller.abort("connection_failed");
			onAbort();
		}, 30 * 1000);

		const response = await fetch(url, { method: "GET", headers, signal: controller.signal });

		if (timer) clearTimeout(timer);

		if (!response.body) {
			throw new Error("ReadableStream not supported");
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder("utf-8");
		let buffer = "";

		while (true) {
			const { done, value } = await reader.read();
			if (done) break;

			buffer += decoder.decode(value, { stream: true });

			// Split SSE events on double newlines
			let parts = buffer.split("\n\n");
			buffer = parts.pop() || "";

			for (let part of parts) {
				if (part.startsWith("data:")) {
					const data = part.replace(/^data:\s*/, "");
					try {
						onMessage({ data: JSON.parse(data) });
					} catch {
						// onMessage({ data });
					}
				} else if (part.startsWith("event:")) {
					const data = part.split("data: ")[1] || "{}";
					try {
						onMessage({ data: JSON.parse(data) });
					} catch {
						// onMessage({ data });
					}
				}
			}
		}
	}

	// Reconnect to pending/downloading jobs on app load and when jobs change
	useEffect(() => {
		const pendingJobs = downloadJobs.filter((job) => job.status === "pending" || job.status === "downloading");

		for (const job of pendingJobs) {
			// Skip if already connected
			if (activeConnections.current.has(job.id)) {
				continue;
			}

			// Mark as connected
			activeConnections.current.add(job.id);

			// Update status to downloading and reconnect to SSE
			dispatch(
				updateDownloadJobStatus({
					jobId: job.id,
					status: "downloading",
					message: job.status === "pending" ? "" : "Reconnecting to download stream..."
				})
			);

			connectSSE(
				`${BASE_API_URL}/sse/stream-fetch?channel=${job.jobResponse.sse_channel}`,
				(event) => {
					const data: IDownloadReportSSEEventData =
						typeof event.data === "string" ? JSON.parse(event.data) : event.data;

					switch (data.type) {
						case "connection_established":
							dispatch(
								updateDownloadJobStatus({
									jobId: job.id,
									status: "downloading",
									progress: 0,
									message: data.message
								})
							);
							break;

						case "report_started":
							dispatch(
								updateDownloadJobStatus({
									jobId: job.id,
									status: "downloading",
									message: data.message
								})
							);
							break;

						case "progress_update":
							dispatch(
								updateDownloadJobStatus({
									jobId: job.id,
									status: "downloading",
									progress: data.progress,
									message: data.message
								})
							);
							break;

						case "report_complete":
							dispatch(
								updateDownloadJobStatus({
									jobId: job.id,
									status: "downloading",
									progress: 100,
									message: data.message,
									error: ""
								})
							);
							// Trigger download
							handleDownloadReport(job.id);
							controller.abort("success");
							break;

						case "report_failed":
							dispatch(
								updateDownloadJobStatus({
									jobId: job.id,
									status: "failed",
									error: data.error,
									message: ""
								})
							);
							// Remove from active connections
							activeConnections.current.delete(job.id);
							controller.abort(data.error);
							break;

						default:
							break;
					}
				},
				() => {
					handleDownloadReport(job.id);
				}
			).catch((error) => {
				console.log("connection lost", error);

				// if (error !== "success" && error !== "connection_failed") {
				// 	dispatch(
				// 		updateDownloadJobStatus({
				// 			jobId: job.id,
				// 			status: "failed",
				// 			error: "Connection lost"
				// 		})
				// 	);
				// }
				// Remove from active connections
				activeConnections.current.delete(job.id);
			});
		}

		// Clean up connections for jobs that are no longer pending/downloading
		const activeJobIds = new Set(pendingJobs.map((job) => job.id));
		activeConnections.current.forEach((jobId) => {
			if (!activeJobIds.has(jobId)) {
				activeConnections.current.delete(jobId);
			}
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [downloadJobs]); // Watch for changes in download jobs

	useEffect(() => {
		return () => {
			controller.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		downloadJobs
	};
}
